import { Component, ElementRef, OnDestroy, ViewChild, Input } from '@angular/core';
import { BrowserCodeReader, BrowserPDF417Reader, BrowserQRCodeReader } from '@zxing/browser';
import { SweetAlertHelper } from '../../../core/helpers/sweet-alert-helper.service';
import { BarcodeFormatEnum, BarcodeScannerEnum } from '../enum/barcode-scanner.enum';
import { BarcodeScannerService } from '../barcode-scanner.service';
import { IScannedDriverLicense } from '../interfaces/barcode-scanner.interface';

@Component({
  selector: 'app-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss']
})
export class ScannerComponent implements OnDestroy {
  @Input() barcodeFormat: BarcodeFormatEnum = BarcodeFormatEnum.Pdf417;
  @ViewChild('video') videoElement: ElementRef;

  constructor(
    private sweetAlterHelper: SweetAlertHelper,
    private barcodeScannerService: BarcodeScannerService
  ) {
    this.startScanner();
  }

  private initializeCodeReader(): BrowserCodeReader {
    switch (this.barcodeFormat) {
      case BarcodeFormatEnum.QrCode:
        return new BrowserQRCodeReader();
      case BarcodeFormatEnum.Pdf417:
        return new BrowserPDF417Reader();
    }
  }

  private requestWebcam(): Promise<void> {
    const mediaDevices = navigator.mediaDevices;
    const getUM = mediaDevices && mediaDevices.getUserMedia;

    if (!getUM) {
      this.sweetAlterHelper.throwErrorAlert('Webcam API is not available or the site is not in a secure context (HTTPS).');
      return Promise.reject(new Error('getUserMedia not available'));
    }

    return getUM.call(mediaDevices, { video: true })
      .then((stream: MediaStream) => {
        stream.getTracks().forEach(track => track.stop());
      })
      .catch((error: any) => {
        this.sweetAlterHelper.captureException(error);
        throw error;
      });
  }

  initScannerApi(): void {
    BrowserCodeReader.listVideoInputDevices()
      .then((devices) => {
        const deviceId = devices.length ? devices[0].deviceId : null;
        const previewElement = this.videoElement.nativeElement;

        if (!deviceId) {
          this.sweetAlterHelper.throwErrorAlert('Please grant permission to webcam');
          return;
        }

        const codeReader: BrowserCodeReader = this.initializeCodeReader();

        codeReader.decodeOnceFromVideoDevice(deviceId, previewElement)
          .then((result) => this.getDataFromScanner(result))
          .catch(error => this.sweetAlterHelper.captureException(error));
      })
  }

  startScanner(): void {
    this.requestWebcam().then(() => this.initScannerApi());
  }

  private getDataFromScanner(result) {
    if (!result) { return; }
    this.sendDataToParentComponent(result.getText());
    this.clearScanner();
  }

  sendDataToParentComponent(content: string): void {
    let action: BarcodeScannerEnum = null;
    let data: IScannedDriverLicense | string = null;

    switch (this.barcodeFormat) {
      case BarcodeFormatEnum.QrCode:
        action = BarcodeScannerEnum.SendBarcodeQrCodeData;
        data = content as string;
        this.barcodeScannerService.setBarcodeScannerAction({ action, data });
        break;
      case BarcodeFormatEnum.Pdf417:
        action = BarcodeScannerEnum.SendBarcodePdf417Data;
        data = this.parseLicenseData(content);
        this.barcodeScannerService.setBarcodeScannerAction({ action, data });
        break;
    }
  }

  clearScanner() {
    const video = this.videoElement.nativeElement;
    if (!video) { return; }

    const stream = video.srcObject as MediaStream;
    if (!stream) { return; }

    stream.getTracks().forEach(track => track.stop());
    video.srcObject = null;

    BrowserCodeReader.cleanVideoSource(video);
  }

  parseLicenseData(rawData: string): IScannedDriverLicense {
    const pattern = /(DAQ|DCA|DBA|DAJ)([^\n\r]*)/g;
    const driverLicenseResponse: IScannedDriverLicense = {};

    let match: string[];

    while ((match = pattern.exec(rawData)) !== null) {
      const identifier = match[1];
      const value = match[2].trim();

      switch (identifier) {
        case "DAQ":
          driverLicenseResponse.licensenumber = value;
          break;
        case "DCA":
          driverLicenseResponse.licenseclass = value;
          break;
        case "DAJ":
          driverLicenseResponse.licensestate = value;
          break;
        case "DBA":
          if (value.length === 8) {
            const month = value.slice(0, 2);
            const day = value.slice(2, 4);
            const year = value.slice(4, 8);

            driverLicenseResponse.licenseexpirationdate = `${month}-${day}-${year}`
          }
          break;
      }
    }

    return driverLicenseResponse;
  }

  ngOnDestroy(): void {
    this.clearScanner();
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/barcode-scanner/scanner/scanner.component.ts